// Hud controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import { type Snapshot, SCANS_PER_BOARD, SCORING } from '../../core';
import { PALETTE, hex, FONT, tex, KEYS } from '../keys';
import { particleBurst, floatText, vignettePulse, mult as multGlyph } from '../juice';
import { audio } from '../audio';
import { motionAllowed, scaledFont, textScale } from '../settings';
import { isLandscape, LANDSCAPE_GUTTER_W } from '../stage';
import { typePx } from '../ui/typeScale';
import { shade } from '../theme';
import { icon, drawIcon, type IconName } from '../icons';
import { labelText, iconButton, heartRow, timerPill } from '../ui/kit';
import { drawRivalMuncher } from '../muncher';
import { PLAYER, AI, comboTier, comboMultiplier } from './types';
import type { GameScene } from '../scenes/GameScene';

// LANDSCAPE STAGE (S4): the inner padding of the left telemetry + right command rails. Shared by
// buildHud (every rail-row anchor) AND buildRivalReadout (its landscape left-anchor) so the whole left
// rail reads as ONE flush-left column — score/quota/hearts already hugged this pad; the review fix pulls
// combo/win-status/rival onto it too (they were centred mid-rail, reading ragged).
const RAIL_PAD = 24;

// ── S9 LANE-G1 HUD POLISH CONFIG (thresholds/gains live here, never inlined) ──────────────────
/** #51 HUD micro-label tracking: eyebrows (SCORE / COMBO) get a stronger arcade letter-spacing so
 *  they read as tracked micro-caps instead of leaning on size + colour alone. Absolute px. */
const HUD_EYEBROW_TRACK_PX = 1.8;
/** M1 win-status pill (the "N TO WIN" / MATCH POINT gold chip). Corner radius + inner padding. */
const WIN_PILL_RAD = 11;
const WIN_PILL_PADX = 12;
const WIN_PILL_PADY = 5;
const WARNING_HINT_PREFIXES = ['DANGER:', 'WARNING:', 'LIAR:', 'SCRAMBLER'] as const;
function isWarningHintText(message: string): boolean {
  return WARNING_HINT_PREFIXES.some((prefix) => message.startsWith(prefix));
}
/** #33 final-5s PANIC escalation: as the last five seconds tick down, the tension veil breathes
 *  FASTER + STRONGER and the timer-bar throb hits harder. Values are the per-second endpoints the
 *  escalation lerps toward (sec 5 → sec 1). Heartbeat is signalled to Lane AUDIO via a game event. */
const PANIC_VEIL_MS = { slow: 560, fast: 240 } as const; // pulse period, sec5 → sec1
/** S9 FIX-C (motion review MAJOR, countdown clip ~00:33): the sec-1 peak used to drive the veil to
 *  FULL alpha, and at full reach the side/bottom gradients washed over EDGE-TILE text at the pulse
 *  peak. 0.85 keeps the accelerating red heartbeat (period still ramps 560→240ms) while the peak
 *  stays under the wash level the stills graded fine at. Band REACH is also pulled outward — see the
 *  vignette geometry in buildHud. reduceMotion path (static veil, no pulse) untouched. */
const PANIC_VEIL_ALPHA = { lo: 0.7, hi: 0.85 } as const; // pulse peak alpha, sec5 → sec1
const PANIC_PILL_PEAK = { lo: 1.06, hi: 1.16 } as const; // per-second timer-pill throb, sec5 → sec1
/** #33 heartbeat event Lane AUDIO subscribes to (game-level so it survives the scene): payload is the
 *  whole-seconds remaining (5..1). Lane AUDIO owns the sound (pitch-rise + boom); G1 only signals. */
const HEARTBEAT_EVENT = 'trivia:heartbeat';
/** #33 panic on/off event (game-level): fired with a bool when the held HUD-tension state flips (last
 *  life OR timer in its final quarter). Lets Lane AUDIO start/stop a heartbeat bed; G1 only signals. */
const PANIC_EVENT = 'trivia:panic';

/** Apply tracked letter-spacing where the Phaser build supports it (3.60+); no-op otherwise. */
function trackLabel(t: Phaser.GameObjects.Text, px: number): void {
  const o = t as unknown as { setLetterSpacing?: (n: number) => unknown };
  o.setLetterSpacing?.(px);
}

/** Repaint the under-board standing strip after the text changes. */
function repaintUnderTextPlate(scene: GameScene): void {
  const t = scene.underText;
  const bg = scene.underTextBg;
  if (!t || !bg) return;
  bg.clear();
  if (!t.text || t.alpha <= 0.01) return;
  const land = isLandscape();
  const maxW = Math.min(scene.scale.width * (land ? 0.46 : 0.86), (scene.boardW || scene.scale.width) * 0.92);
  const w = Math.min(maxW, t.displayWidth + 28);
  const h = Math.max(28, t.displayHeight + 12);
  const x = t.x - w / 2;
  const y = t.y - h / 2;
  bg.fillStyle(PALETTE.void, 0.82);
  bg.fillRoundedRect(x, y, w, h, 12);
  bg.lineStyle(1.25, PALETTE.scan, 0.42);
  bg.strokeRoundedRect(x, y, w, h, 12);
}

  // --- HUD -----------------------------------------------------------------
export function buildHud(this: GameScene, snap: Snapshot): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const bannerH = Math.min(72, H * 0.1);
    const hudTopH = Math.min(64, H * 0.09);
    const hudY = bannerH + hudTopH / 2;
    // LANDSCAPE STAGE (S4): the top HUD row + bottom dock are re-anchored into two gutter rails flanking
    // the centred board — LEFT = telemetry (score/quota/combo/win-status/rival/hearts), RIGHT = command
    // (timer + SCAN/CHOMP). Each `land ? … : ORIGINAL` collapses to the exact portrait expression when
    // land is false, so the shipped phone layout is byte-identical. GUTTER_W matches computeLayout.
    const land = isLandscape();
    const gutterW = LANDSCAPE_GUTTER_W;
    const railPad = RAIL_PAD;
    const rightRailCx = W - gutterW / 2;
    const railTop = 118; // first telemetry-rail row, clear of the marquee rule bar
    const leftX = land ? railPad : Math.max(10, W * 0.05);
    const rightX = W - Math.max(10, W * 0.05);
    // Left-rail vertical stack Ys (portrait keeps the single-row anchors).
    const scoreLabelY = land ? railTop : hudY - 16;
    const scoreValY = land ? railTop + 26 : hudY + 10;
    const quotaY = land ? railTop + 58 : hudY + 41;
    // Landscape rail rows are FLUSH-LEFT to railPad (origin 0) so the column reads as one design; portrait
    // keeps the centred single-row anchors. (comboCx doubles as the eyebrow x; the bolt+×N sit right of it.)
    const comboCx = land ? railPad : W / 2;
    const comboY = land ? railTop + 128 : hudY + 9;
    const comboSubY = land ? comboY + 20 : hudY + 39;
    const winCx = land ? railPad : W / 2;
    const boardTopY = this.gridY > 0 ? this.gridY : bannerH + hudTopH + 126;
    const portraitWinY = Math.min(bannerH + hudTopH + 84, boardTopY - 26);
    const winY = land ? railTop + 192 : portraitWinY;
    const rivalY = land ? railTop + 262 : hudY + 6;
    const livesLabelY = land ? railTop + 72 : quotaY + 19;
    const heartsY = land ? railTop + 94 : quotaY + 38;

    // ── TENSION VEIL — held red EDGE vignette (near-miss); keeps the board centre readable, unlike a
    // flat full-screen wash. Hidden until lives<=1 or the timer is low. Alpha/pulse set in setTensionState.
    // Depth 0.5: ABOVE the backdrop but BELOW all HUD telemetry + board content, so the near-miss
    // red frames the screen edges WITHOUT obscuring the score/combo/rival/dock (which stay on top).
    const tv = this.add.graphics().setDepth(0.5).setScrollFactor(0).setVisible(false);
    const hz = PALETTE.hazard;
    // S9 FIX-C (panic-veil motion legibility): band REACH pulled outward so the gradients zero out
    // BEFORE the edge-tile text zone — portrait side bands (0.14W) used to run under the first/last
    // tile columns and the landscape bottom band (0.15H) crossed the near-full-height board's bottom
    // row, so at panic peak the breathing red washed live labels. 0.10W sides keep the frame; the
    // bottom band drops to 0.08H in landscape (board bottom ~0.96H there) / 0.12H portrait (board
    // ends well above the dock band, so the deeper reach is safe and keeps the drama).
    const vh = Math.round(H * (land ? 0.08 : 0.12));
    const vw = Math.round(W * 0.1);
    // Top band kept WEAK (the HUD telemetry lives here — keep its dark backing for legibility);
    // the tension frame is carried mainly by the sides + bottom.
    tv.fillGradientStyle(hz, hz, hz, hz, 0.32, 0.32, 0, 0);
    tv.fillRect(0, 0, W, vh); // top
    tv.fillGradientStyle(hz, hz, hz, hz, 0, 0, 0.82, 0.82);
    tv.fillRect(0, H - vh, W, vh); // bottom
    tv.fillGradientStyle(hz, hz, hz, hz, 0.7, 0, 0.7, 0);
    tv.fillRect(0, 0, vw, H); // left
    tv.fillGradientStyle(hz, hz, hz, hz, 0, 0.7, 0, 0.7);
    tv.fillRect(W - vw, 0, vw, H); // right
    this.tensionVeil = tv;

    // ── TENSION WARMTH — a low-alpha warm plum wash (mockup: the backdrop radial shifts toward
    // #1A1320 under tension). Depth 0.4: above the backdrop (-10), below the veil (0.5) and every
    // HUD/board layer. setTensionState fades it in/out slowly — a wash, never a flash.
    this.tensionWarmth = this.add
      .rectangle(W / 2, H / 2, W, H, 0x1a1320, 1)
      .setDepth(0.4)
      .setScrollFactor(0)
      .setAlpha(0)
      .setVisible(false);

    // ── LEFT ZONE — SCORE label / value (Chakra 700, player, cyan glow) / quota pips ──
    // #51: the micro-label eyebrows get stronger tracking so they read as designed caps, not size+color alone.
    trackLabel(labelText(this, leftX, scoreLabelY, 'SCORE', { size: land ? 9 : 10, color: PALETTE.dim }).setDepth(2), HUD_EYEBROW_TRACK_PX);
    this.scoreText = this.add
      .text(leftX, scoreValY, '0', {
        fontFamily: FONT.display,
        fontSize: `${land ? Math.min(30, scaledFont(26, 18)) : Math.min(42, scaledFont(36, 24))}px`,
        color: hex(PALETTE.player),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setShadow(0, 0, hex(PALETTE.player), 8, false, true)
      .setDepth(2);
    this.buildQuotaChip(snap, leftX, quotaY);

    // ── CENTER ZONE — bolt + xN multiplier (Chakra 700, correct) over a "COMBO" eyebrow ──
    // Landscape: left-origin ×N seated right of a rail-flush bolt (icon left edge ≈ railPad); portrait
    // keeps the centred bolt+×N pair. The bolt's live position is re-derived in updateHud (land-aware).
    this.comboText = this.add
      .text(land ? comboCx + 26 : comboCx + 6, comboY, '', {
        fontFamily: FONT.display,
        fontSize: `${land ? Math.min(28, scaledFont(24, 16)) : Math.min(38, scaledFont(32, 22))}px`,
        color: hex(PALETTE.correct),
        fontStyle: 'bold',
      })
      .setOrigin(land ? 0 : 0.5, 0.5)
      .setDepth(2);
    this.comboIcon = icon(this, 'bolt', land ? comboCx + 8 : comboCx - 22, comboY, land ? 17 : 22, PALETTE.correct).setDepth(2).setVisible(false);
    this.comboSubLabel = labelText(this, comboCx, comboSubY, 'COMBO', { size: land ? 8 : 11, color: PALETTE.dim, origin: land ? 0 : 0.5 })
      .setDepth(2)
      .setVisible(false);
    trackLabel(this.comboSubLabel, HUD_EYEBROW_TRACK_PX); // #51 micro-label tracking

    // ── RIGHT ZONE — rival pill (magenta triangle + score). Player lives stay with SCORE on the left. ──
    if (this.aiEnabled) this.buildRivalReadout(snap, rivalY);
    // GHOST DUELS Slice A: the spectral self-race score chip is right-anchored just BELOW the hearts,
    // grouped with the rival readout (only built when a ghost racer exists — the daily self-race).
    const heartSize = land ? 15 : 19;
    const heartGap = land ? 5 : 6;
    const heartsW = 3 * (heartSize + heartGap) - heartGap;
    // #34: hearts imply a losable board, so gate them exactly like the timer bar (GameScene.create
    // `timerVisible = !noviceRun || boardIndex > 0`). The first training board is UNTIMED + hazard-free —
    // a life meter there advertises an impossible fail state to a brand-new player. Boards 1+ (timed,
    // hazards land) keep the meter. init() already leaves heartRowC/heartRowSet undefined + heartGs [];
    // every consumer is null-safe (heartRowSet?.(), heartGs[0] guarded, heartRowC guarded).
    const showHearts = !this.noviceRun || this.boardIndex > 0;
    if (showHearts) {
      const livesLabel = labelText(this, leftX, livesLabelY, 'LIVES', { size: land ? 8 : 9, color: PALETTE.dim }).setDepth(2);
      trackLabel(livesLabel, HUD_EYEBROW_TRACK_PX);
      const hr = heartRow(this, 0, heartsY, 3, { size: heartSize, gap: heartGap });
      hr.container.setX(leftX).setDepth(2);
      this.heartRowC = hr.container;
      this.heartRowSet = hr.set;
      // HUD-TENSION: keep the per-heart Graphics handles so the last-life heart can pulse (mockup parity).
      this.heartGs = hr.hearts;
    }
    // The ghost self-race chip anchor is independent of the heart meter (it sits where the hearts WOULD
    // be), so compute it in both cases so a novice-board-0 daily-ghost chip still lands correctly.
    if (land) {
      this.ghostChipAnchor = { x: railPad + heartsW, y: heartsY + 30 };
    } else {
      this.ghostChipAnchor = { x: rightX, y: (this.aiEnabled ? hudY + 16 : hudY) + 26 };
    }
    this.heartSize = heartSize;

    // "Distance from win" verdict — a centered plain-language token in the gap above the grid.
    // M1: the verdict now rides a PILL (gold when ahead → "N TO WIN"/"MATCH POINT" reads as the reward
    // state; hazard-tinted when behind). The pill graphics sits just under the text; both re-laid every
    // change by updateWinStatus. Built here (behind the text at depth 1.9) so create()-time draws clean.
    this.winStatusPill = this.add.graphics().setDepth(1.9);
    const winFs = Math.min(15, scaledFont(13, 11));
    this.winStatusText = this.add
      .text(winCx, winY, '', { fontFamily: FONT.display, fontSize: `${winFs}px`, color: hex(PALETTE.textDim), fontStyle: 'bold' })
      .setOrigin(land ? 0 : 0.5, 0.5)
      .setDepth(2);

    // "Under you:" — mirror the human's standing-cell label below the board, as a strip, so the
    // muncher can never hide the tile name it is standing on.
    const underBoardBottom = this.gridY + this.cellH * this.rows;
    const underY = underBoardBottom + (land ? 18 : 18);
    this.underTextBg = this.add.graphics().setDepth(3.8);
    this.underText = this.add
      .text(W / 2, underY, '', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'caption', { vw: 0.024 })}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: Math.min((this.boardW || W) * 0.86, W * 0.84) },
      })
      .setOrigin(0.5, 0.5)
      .setDepth(4);

    // ── TIMER PILL — clock + armed→correct gradient pill + tabular m:ss ──
    // Portrait: centred below the board, above the dock. Landscape: seated in the RIGHT command rail.
    const bottomH = Math.max(120, Math.min(150, H * 0.2));
    const dockTop = H - bottomH;
    const boardBottom = this.gridY + this.cellH * this.rows;
    const pillCx = land ? rightRailCx : W / 2;
    const pillY = land ? 300 : Phaser.Math.Clamp((boardBottom + dockTop) / 2, boardBottom + 18, dockTop - 14);
    const pillW = land ? gutterW - railPad * 2 : Math.min(W * 0.9, 460);
    // Timer LOW-state glow (mockup: the solid-hazard fill gains a soft red halo once time is low).
    // Drawn once BEHIND the pill (depth 2.9); shown/pulsed by updateTimerLowState only.
    const tg = this.add.graphics().setDepth(2.9).setBlendMode(Phaser.BlendModes.ADD).setVisible(false);
    tg.fillStyle(PALETTE.hazard, 0.05);
    tg.fillRoundedRect(-pillW / 2 - 10, -15, pillW + 20, 30, 15);
    tg.fillStyle(PALETTE.hazard, 0.09);
    tg.fillRoundedRect(-pillW / 2 - 4, -10, pillW + 8, 20, 10);
    tg.setPosition(pillCx, pillY);
    this.timerGlow = tg;
    const tp = timerPill(this, pillCx, pillY, pillW);
    tp.container.setDepth(3).setVisible(this.timerVisible);
    this.timerPillC = tp.container;
    this.timerPillSet = tp.set;

    // ── COMMAND DOCK — SCAN (scan outline + charge badge) + CHOMP (green fill) ──
    // Dock button geometry, computed up front so the toast anchor can clear it (item 5 fix below).
    // Lift the dock above the gesture/home-indicator band: a comfortable baseline margin ON TOP of any
    // env(safe-area-inset-bottom) the page already insets the canvas by, so SCAN/CHOMP never hug the edge.
    const btnH = land ? 58 : Math.max(52, Math.min(62, H * 0.05));
    const gestureSafePad = Math.max(86, Math.min(112, H * 0.1));
    const btnY = H - gestureSafePad - btnH / 2;
    const landChompY = H - 96;
    const landScanY = landChompY - btnH - 20;
    const landScanTop = landScanY - btnH / 2;
    // S3 item 2: THE single anchored toast slot. Landscape floats the toast just below the centred board
    // (the dock lives in the side rail, so no dock-collision clamp is needed there). Portrait keeps the
    // dockTop anchor + the 150%-text-scale clearance clamp so the SKIPPED pill never grazes the dock.
    if (land) {
      // S9 FIX-C CAPTION-BAND ARBITER (G0 AMATEURs 3-7 / G1 item 5): the landscape board column is
      // near-FULL-HEIGHT, so the old "below the board, clamped above the bottom margin" anchor always
      // resolved to a fixed y INSIDE the bottom tile row — coach lines rendered through tile labels on
      // the 6-row daily and through the player sprite + FIRST BITE burst on the novice board. Dock the
      // ONE toast slot in the RIGHT command rail instead, centred ABOVE the SCAN/CHOMP stack (the verbs
      // it coaches): structurally off the board, never over the CTAs. toastSlotX carries the rail centre
      // to every slot consumer (hint line, SKIPPED pill, verb floats).
      const lineH = typePx(this, 'body', { vw: 0.038, min: 15 }) * 1.25;
      this.toastSlotY = landScanTop - 16 - lineH * 1.5 - 8; // clears a 3-line wrapped caption above SCAN
      this.toastSlotX = rightRailCx;
    } else {
      const ts = textScale();
      const dockButtonTop = btnY - btnH / 2;
      const pillHalfH = typePx(this, 'body', { vw: 0.042, min: 13 }) * 0.7 + 8; // ~half the SKIPPED pill height
      this.toastSlotY = Math.min(dockTop + 18, dockButtonTop - 6 * ts - pillHalfH);
      this.toastSlotX = W / 2;
    }
    this.commandHintBg = this.add.graphics().setDepth(2.95).setScrollFactor(0);
    this.commandHintText = this.add
      .text(this.toastSlotX, this.toastSlotY, '', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'body', { vw: 0.038, min: 15 })}px`,
        color: hex(0xffffff),
        align: 'center',
        // Landscape captions wrap INSIDE the command rail (they live there now); portrait keeps 0.9W.
        wordWrap: { width: land ? gutterW - 24 : W * 0.9 },
      })
      .setOrigin(0.5)
      .setStroke('#06111f', 3)
      .setDepth(3);
    if (land) this.commandHintText.setMaxLines(3);

    if (land) {
      // RIGHT command rail: SCAN + CHOMP stacked vertically (CHOMP primary + lower, thumb/cursor-reachable;
      // pause stays docked on the marquee bar). Full-rail width so both verbs are large hit targets.
      const dockCx = rightRailCx;
      const btnW = gutterW - railPad * 2;
      const chompY = landChompY;
      const scanY = landScanY;
      if (this.scanUnlocked) {
        this.scanButton = this.buildCommandButton(
          dockCx,
          scanY,
          btnW,
          btnH,
          'SCAN',
          multGlyph(SCANS_PER_BOARD), // #46: one glyph everywhere — always ×, never x
          PALETTE.scan,
          PALETTE.scan,
          () => this.dispatch('scan'),
        );
      } else {
        this.scanText = this.add
          .text(0, 0, '', { fontFamily: FONT.family, fontSize: '1px', color: hex(PALETTE.bg) })
          .setVisible(false);
        this.scanButton = this.add.container(dockCx, scanY, [this.scanText]).setVisible(false).setActive(false);
      }
      this.munchButton = this.buildCommandButton(
        dockCx,
        chompY,
        btnW,
        btnH,
        'CHOMP',
        '',
        PALETTE.correct,
        PALETTE.white,
        () => this.dispatch('munch'),
      );
    } else {
      const btnGap = Math.max(10, W * 0.03);
      const dockW = W * 0.9;
      const startX = (W - dockW) / 2;
      const scanW = this.scanUnlocked ? Math.max(116, Math.min(dockW * 0.4, dockW - btnGap - 150)) : 0;
      const chompW = this.scanUnlocked ? dockW - scanW - btnGap : dockW;
      if (this.scanUnlocked) {
        this.scanButton = this.buildCommandButton(
          startX + scanW / 2,
          btnY,
          scanW,
          btnH,
          'SCAN',
          multGlyph(SCANS_PER_BOARD), // #46: one glyph everywhere — always ×, never x
          PALETTE.scan,
          PALETTE.scan,
          () => this.dispatch('scan'),
        );
      } else {
        this.scanText = this.add
          .text(0, 0, '', { fontFamily: FONT.family, fontSize: '1px', color: hex(PALETTE.bg) })
          .setVisible(false);
        this.scanButton = this.add.container(startX + dockW, btnY, [this.scanText]).setVisible(false).setActive(false);
      }
      this.munchButton = this.buildCommandButton(
        startX + (this.scanUnlocked ? scanW + btnGap : 0) + chompW / 2,
        btnY,
        chompW,
        btnH,
        'CHOMP',
        '',
        PALETTE.correct,
        PALETTE.white,
        () => this.dispatch('munch'),
      );
    }

    this.updateHud(snap);
  }


  /**
   * Quota progress chip: a row of N small pips that FILL with the correct-accent as the player eats
   * correct cells, plus a tiny "filled/quota" count. Left-anchored at (leftX, y) and width-capped so it
   * never crosses the rail's centre (where COMBO lives) at 360px. Empty pips read as hollow outlines.
   */
export function buildQuotaChip(this: GameScene, snap: Snapshot, leftX: number, y: number): void {
    // Tactical-OLED kit-pip look: visible 6-segment quota meter, filled = correct accent, empty = tile.
    const quota = Math.max(1, snap.quota);
    const pipW = 24;
    const pipH = 8;
    const gap = 4;
    this.quotaPips = [];
    let px = leftX + pipW / 2;
    for (let i = 0; i < quota; i++) {
      const r = this.add.rectangle(px, y, pipW, pipH, PALETTE.tile, 1).setDepth(2);
      r.setStrokeStyle(1, PALETTE.lineStrong, 1);
      this.quotaPips.push(r);
      px += pipW + gap;
    }
  }


  /** Repaint the quota pips for `eaten` filled of `quota`, popping the newest-filled pip on a change. */
export function updateQuotaChip(this: GameScene, eaten: number, quota: number): void {
    if (this.quotaPips.length === 0) return;
    const filled = Phaser.Math.Clamp(eaten, 0, this.quotaPips.length);
    const met = filled >= quota;
    for (let i = 0; i < this.quotaPips.length; i++) {
      const on = i < filled;
      const pipColor = met ? PALETTE.armed : PALETTE.correct;
      this.quotaPips[i].setFillStyle(on ? pipColor : PALETTE.tile, 1);
      this.quotaPips[i].setStrokeStyle(1, on ? pipColor : PALETTE.lineStrong, 1);
    }
    // Pop the freshly-filled pip (motion-gated) so each correct eat lands a tiny celebratory tick.
    if (eaten > this.prevEatenCorrect && this.prevEatenCorrect >= 0 && filled > 0 && motionAllowed()) {
      const justFilled = this.quotaPips[filled - 1];
      if (justFilled?.active) {
        this.tweens.add({
          targets: justFilled,
          scale: { from: 1.6, to: 1 },
          duration: 220,
          ease: 'Back.easeOut',
        });
      }
    }
    this.prevEatenCorrect = eaten;
  }


  /**
   * WARN-1 RIVAL READOUT — a compact, always-visible chip that makes the AI rival feel like a live
   * competitor during play (its score was previously only on the result screen). It lives INSIDE the
   * top HUD rail, top-RIGHT, mirroring the human SCORE on the top-LEFT — so the two read as a head-to-
   * head on one line, and the chip is STRUCTURALLY above the grid (it can never occlude the answer
   * cells, the rule banner, or the centered "YOU ARE ON:" label near the grid). Right-anchored and
   * width-capped so it stays clear of the centered COMBO. Double-coded: a magenta "RIVAL ▼<score>"
   * (colour + a ▼ shape glyph + the word — never colour alone) plus a signed, colour-coded lead gap so
   * the player reads the margin at a glance. Built once per board; children die with the scene.
   */
export function buildRivalReadout(this: GameScene, snap: Snapshot, hudY: number): void {
    const W = this.scale.width;
    // S3 item 7: the pill's right edge + its (1.5px) tension border + the persona face (which pokes a
    // touch above/around the pill) must never kiss the right viewport edge. A ≥52px / 10vw right inset
    // keeps the whole chip — including live score-pulse scale and emote overflow — clear of the edge.
    // Landscape: the readout is LEFT-anchored flush to railPad so it joins the one rail column (review
    // fix — it previously right-anchored mid-rail and read ragged); the pill grows RIGHT. Portrait keeps
    // the top-right right-anchor (pill grows left, clear of the centred COMBO + the right edge).
    const land = isLandscape();
    const anchorX = land ? RAIL_PAD : W - Math.max(72, W * 0.15);
    const c = this.add.container(anchorX, hudY).setDepth(2);
    const bg = this.add.graphics();
    const tri = this.add.graphics();
    drawRivalMuncher(tri, land ? 14 : 18, { glow: false });
    const fs = land ? Math.min(18, scaledFont(15, 12)) : Math.min(34, scaledFont(30, 20));
    const label = this.add
      .text(0, 0, 'RIVAL', {
        fontFamily: FONT.display,
        fontSize: `${land ? 9 : 11}px`,
        color: hex(PALETTE.ai),
        fontStyle: '700',
      })
      .setOrigin(land ? 0 : 1, 0.5);
    trackLabel(label, HUD_EYEBROW_TRACK_PX);
    this.rivalScoreText = this.add
      .text(0, 0, '', { fontFamily: FONT.display, fontSize: `${fs}px`, color: hex(PALETTE.ai), fontStyle: 'bold' })
      .setOrigin(land ? 0 : 1, 0.5);
    c.add([bg, label, tri, this.rivalScoreText]);
    c.setData('tri', tri);
    c.setData('rivalLabel', label);
    // S6 IDENTITY (Decision 4 — ONE chip/board/portrait): the readout's RESTING avatar is BYT3's
    // board-token CREATURE (idle pose) when the pose set decoded — mirroring makeAiSprite's priority
    // (pose → … → geometric triangle) — so the chip reads as the same character the board token is.
    // The pose image is sized to the triangle's ~18px slot so the pill layout math (triW) is
    // undisturbed; updateRivalReadout's dirty-check relayout repositions it alongside `tri`.
    let poseChip = false;
    if (this.rivalPosesReady()) {
      const av = this.add.image(0, 0, tex(KEYS.rivalPoseIdle)).setOrigin(0.5);
      const avSize = land ? Math.round(fs * 1.5) : 28;
      av.setScale(avSize / Math.max(av.width || 1, av.height || 1));
      c.add(av);
      c.setData('rivalChipAvatar', av);
      tri.setVisible(false);
      poseChip = true;
    }
    // Persona emote face: still built as the caption anchor + gloat/panic expression surface. When the
    // pose chip owns the resting identity it rides HIDDEN (bounds stay valid for captions); otherwise it
    // is the visible emote-portrait fallback that replaces the triangle (2.8× the score font so the
    // expression reads on a phone). Geometric triangle stays only as the decode-failure last resort.
    if (this.rivalPersona?.buildFace(c, 0, 0, land ? Math.round(fs * 2.8) : 34, { visible: !poseChip }) && !poseChip) {
      tri.setVisible(false);
    }
    // "Thinking" dots left of the pill while the rival waits out its board-start grace (a graced
    // rival otherwise reads as ABSENT — feel-retune review MINOR). Gentle alpha breathe, motion-gated.
    this.rivalThinkingText = this.add
      .text(0, 0, '…', { fontFamily: FONT.display, fontSize: `${fs}px`, color: hex(PALETTE.ai), fontStyle: 'bold' })
      .setOrigin(land ? 0 : 1, 0.5)
      .setAlpha(0.85)
      .setVisible(false);
    c.add(this.rivalThinkingText);
    if (motionAllowed()) {
      this.tweens.add({ targets: this.rivalThinkingText, alpha: 0.35, duration: 620, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    this.rivalReadout = c;
    this.rivalReadoutBg = bg;
    // First paint (immediate=true so the create()-time screenshot is correct and never flashes).
    this.updateRivalReadout(snap, true);
  }


  /**
   * Per-frame refresh of the rival pill from the live snapshot: a small magenta triangle + the rival's
   * score, in a surface pill bordered rival@0.4 (solid rival under tension). Pulses (motion-gated) when
   * the rival scores or the lead flips. Cheap: relayout/redraw run ONLY on a real change (dirty-checked).
   */
export function updateRivalReadout(this: GameScene, snap: Snapshot, immediate = false): void {
    if (!this.rivalReadout || !this.rivalReadoutBg || !this.rivalScoreText) {
      return;
    }
    const human = snap.players.find((p) => p.id === PLAYER);
    const rival = snap.players.find((p) => p.id === AI);
    if (!human || !rival) return;
    // "Thinking" tell runs every frame (independent of the dirty-check): visible from board start
    // until the rival's first act, capped a beat past the grace so it never lingers.
    if (this.rivalThinkingText) {
      const suppressThinkingUntil = Number(this.rivalReadout.getData('suppressThinkingUntil') ?? 0);
      const show =
        this.time.now >= suppressThinkingUntil &&
        this.aiEnabled &&
        !this.rivalHasActed &&
        snap.status === 'playing' &&
        snap.timeMs <= this.rivalGraceMs + 900;
      if (this.rivalThinkingText.visible !== show) this.rivalThinkingText.setVisible(show);
      this.rivalPersona?.setThinking(show);
    }
    const lead = human.score - rival.score;
    const leadSign = lead > 0 ? 1 : lead < 0 ? -1 : 0;

    // Dirty-check: nothing visible changed since the last paint → skip the relayout + graphics rebuild.
    const changed = immediate || rival.score !== this.prevRivalScore || lead !== this.prevLead || this.tensionOn;
    if (!changed) return;

    // A rival decoy penalty can temporarily push the raw score below zero. Display the competitive
    // standing as a floor-clamped score and let the dedicated "0 FLOOR" badge carry the penalty beat;
    // otherwise the negative score text collides with the inline RIVAL label in the landscape rail.
    this.rivalScoreText.setText(Math.max(0, rival.score).toLocaleString('en-US')).setColor(hex(PALETTE.ai));
    const land = isLandscape();
    const padX = land ? 11 : 13;
    const triW = land ? 18 : 24;
    const gap = land ? 6 : 8;
    const scoreW = this.rivalScoreText.width;
    const tri = this.rivalReadout.getData('tri') as Phaser.GameObjects.Graphics | undefined;
    const label = this.rivalReadout.getData('rivalLabel') as Phaser.GameObjects.Text | undefined;
    // S6: the BYT3 pose-creature chip avatar (when present) tracks the SAME slot the triangle would,
    // so the pill's score-width relayout keeps the identity centred in both rail variants.
    const chipAvatar = this.rivalReadout.getData('rivalChipAvatar') as Phaser.GameObjects.Image | undefined;
    const contentW = triW + gap + scoreW;
    const pillW = contentW + padX * 2;
    const pillH = Math.max(land ? 26 : 38, this.rivalScoreText.height + (land ? 10 : 12));
    this.rivalReadoutBg.clear();
    this.rivalReadoutBg.fillStyle(PALETTE.surface, 1);
    if (land) {
      // LEFT-anchored (rail column): triangle/face first, score to its right, pill grows RIGHT from x=0.
      const triCx = padX + triW / 2;
      tri?.setPosition(triCx, 0);
      chipAvatar?.setPosition(triCx, 0);
      this.rivalPersona?.layout(triCx, 0); // face sits where the triangle did
      this.rivalScoreText.setX(triCx + triW / 2 + gap);
      // In phone-landscape the MATCH POINT chip sits one rail row above this readout; placing RIVAL as
      // an eyebrow above the pill crowds that row. Keep the label in-line, just after the score pill.
      label?.setPosition(pillW + 10, 0);
      this.rivalThinkingText?.setX(triCx + triW / 2 + gap + scoreW + 8); // dots just right of the score
      this.rivalReadoutBg.fillRoundedRect(0, -pillH / 2, pillW, pillH, pillH / 2);
      this.rivalReadoutBg.lineStyle(1.5, PALETTE.rival, this.tensionOn ? 1 : 0.4);
      this.rivalReadoutBg.strokeRoundedRect(0, -pillH / 2, pillW, pillH, pillH / 2);
    } else {
      // Right-anchored layout: score at the right edge, the triangle to its left.
      this.rivalScoreText.setX(-padX);
      tri?.setPosition(-padX - scoreW - gap - triW / 2, 0);
      chipAvatar?.setPosition(-padX - scoreW - gap - triW / 2, 0);
      this.rivalPersona?.layout(-padX - scoreW - gap - triW / 2, 0); // face sits where the triangle did
      if (textScale() >= 1.25) {
        label?.setVisible(false);
      } else {
        label?.setVisible(true).setPosition(-padX, -pillH / 2 - 12);
      }
      this.rivalThinkingText?.setX(-padX - scoreW - gap - triW - 8); // dots sit just left of the triangle
      this.rivalReadoutBg.fillRoundedRect(-pillW, -pillH / 2, pillW, pillH, pillH / 2);
      this.rivalReadoutBg.lineStyle(1.5, PALETTE.rival, this.tensionOn ? 1 : 0.4);
      this.rivalReadoutBg.strokeRoundedRect(-pillW, -pillH / 2, pillW, pillH, pillH / 2);
    }

    if (immediate) {
      this.prevRivalScore = rival.score;
      this.prevLead = lead;
      this.prevLeadSign = leadSign;
      return;
    }
    // Pulse on a real change: the rival just scored, OR the lead flipped. Motion-gated; a single short
    // scale tween on the chip (calm static no-op under reduced-motion).
    const rivalScored = this.prevRivalScore >= 0 && rival.score > this.prevRivalScore;
    const leadFlipped = this.prevLeadSign !== 0 && leadSign !== 0 && leadSign !== this.prevLeadSign;
    // Rival score feedback is the readout update + chip pulse below. Floating +N ticks repeatedly
    // crossed MATCH POINT / rule-banner text in narrow captures, so routine score deltas stay inside the
    // persistent score chip instead of spawning transient text in adjacent HUD lanes.
    if ((rivalScored || leadFlipped) && motionAllowed() && this.rivalReadout.active) {
      this.tweens.killTweensOf(this.rivalReadout);
      this.rivalReadout.setScale(1);
      this.tweens.add({
        targets: this.rivalReadout,
        scale: { from: leadFlipped ? 1.16 : 1.09, to: 1 },
        duration: leadFlipped ? 320 : 220,
        ease: 'Back.easeOut',
        onComplete: () => this.rivalReadout?.setScale(1),
      });
      // Promote a lead FLIP from the 1px chip wiggle to a centered callout, so pulling ahead of (or
      // losing the lead to) the rival reads as a beat rather than a stat tick. Gated with the pulse.
      if (leadFlipped) {
        // BYT3 reacts to the flip (gloat when it takes the lead, panic when it loses it) — persona
        // ignores micro-churn near zero via its own gap threshold.
        this.rivalPersona?.reactLeadChange(leadSign, lead);
        // STRETCH A3 #44: a lead FLIP is the "took the lead" menace trigger. The broadcast beat adds the
        // VISUAL layer (edge tint + portrait gloat flare + one-focal-point aiPressureCue dim) with its own
        // throttle; the persona above already owns the bark on the flip, so onLeadFlip never re-barks.
        this.broadcast?.onLeadFlip(snap, leadSign, lead);
        // S6: the board-token pose latches — TAUNT while the rival holds the lead, back to IDLE once
        // the human pulls ahead (a real synced tell of who is winning, not just a stat wiggle).
        if (Math.abs(lead) >= 60) this.setRivalPose(leadSign < 0 ? 'taunt' : 'idle');
        floatText(
          this,
          this.scale.width / 2,
          // Above the board entirely (store-cut review: the banner brushed row-1 tile text) — the
          // float's own spawn-lift + rise then carry it further INTO the HUD band, never over tiles.
          this.gridY - Math.max(36, this.cellH * 0.55),
          leadSign > 0 ? 'YOU PULLED AHEAD!' : 'RIVAL TOOK THE LEAD',
          hex(leadSign > 0 ? PALETTE.correct : PALETTE.hazard),
          18,
          { clearance: 'high' },
        );
      }
    }
    this.prevRivalScore = rival.score;
    this.prevLead = lead;
    this.prevLeadSign = leadSign;
  }


  /**
   * "DISTANCE FROM WIN" — the synthesized verdict line. Fuses the two axes that actually decide the
   * board: quota-to-go (the END trigger) and the score margin vs the AI (the WIN determinant). Crucial
   * subtlety from Core: hitting quota ENDS the board and the higher score at that instant wins — so
   * clearing while BEHIND loses. The copy reflects that: when ahead it says "N TO WIN" / "MATCH POINT"
   * (clear to clinch); when behind it says "BEHIND N" (catch up first, don't rush the clear). Also fires
   * the near-quota buildup callout once on the toGo→1 step. Cheap: text + an occasional one-shot float.
   */
export function updateWinStatus(this: GameScene, snap: Snapshot): void {
    if (!this.winStatusText) return;
    const human = snap.players.find((p) => p.id === PLAYER);
    if (!human) return;
    const toGo = Math.max(0, snap.quota - human.eatenCorrect);
    const rival = this.aiEnabled ? snap.players.find((p) => p.id === AI) : undefined;
    const lead = rival ? human.score - rival.score : 0;

    let label: string;
    let color: number;
    const quotaLabel = `QUOTA ${Math.min(human.eatenCorrect, snap.quota)}/${snap.quota}`;
    if (toGo <= 0) {
      label = quotaLabel;
      color = PALETTE.armed;
    } else if (!rival) {
      label = quotaLabel;
      color = toGo <= 1 ? PALETTE.armed : PALETTE.correct;
    } else if (lead > 0) {
      // Ahead: clearing the quota clinches the board, so push toward the clear.
      label = toGo <= 1 ? 'MATCH POINT' : quotaLabel;
      color = toGo <= 1 ? 0xffd54a : PALETTE.correct;
    } else if (lead < 0) {
      // Behind: clearing now would END the board while you're losing — tell them to catch up first.
      label = `DOWN ${-lead} · ${Math.min(human.eatenCorrect, snap.quota)}/${snap.quota}`;
      color = PALETTE.hazard;
    } else {
      label = `TIED · ${Math.min(human.eatenCorrect, snap.quota)}/${snap.quota}`;
      color = PALETTE.armed;
    }
    // M1: reward states ("N TO WIN" / "MATCH POINT" / "CLEAR!") ride a GOLD chip + gold text so the
    // winnable state reads as the reward framing the mock calls for; behind/tied stay non-reward.
    const reward = toGo <= 0 || !rival || lead > 0;
    const behind = !!rival && lead < 0;
    const textColor = reward ? (toGo <= 1 ? 0xffd54a : PALETTE.gold) : color;
    this.winStatusText.setText(label).setColor(hex(textColor));
    // M1 GOLD PILL — pure geometry off the live (unscaled) text bounds so the toGo→1 text pop never
    // jitters the chip; dark backing keeps the verdict legible over any board. reduceMotion/text-scale safe.
    const pillColor = reward ? PALETTE.gold : behind ? PALETTE.hazard : PALETTE.armed;
    const gp = this.winStatusPill;
    if (gp && this.winStatusText.text.length > 0) {
      const t = this.winStatusText;
      const w = t.width + WIN_PILL_PADX * 2;
      const h = t.height + WIN_PILL_PADY * 2;
      const left = t.originX < 0.5 ? t.x - WIN_PILL_PADX : t.x - w / 2;
      const top = t.y - h / 2;
      gp.clear();
      gp.fillStyle(PALETTE.void, 0.6);
      gp.fillRoundedRect(left, top, w, h, WIN_PILL_RAD);
      if (reward) {
        gp.fillStyle(pillColor, 0.14); // gold wash: the winnable state glows as a reward chip
        gp.fillRoundedRect(left, top, w, h, WIN_PILL_RAD);
      }
      gp.lineStyle(1.5, pillColor, reward ? 0.95 : behind ? 0.7 : 0.55);
      gp.strokeRoundedRect(left, top, w, h, WIN_PILL_RAD);
    }

    // Buildup: one correct away from ENDING the board is the single most decision-heavy instant. Fire a
    // one-time centered callout on the toGo→1 transition, framed by who's ahead so it teaches the rule.
    if (this.prevToGo > 1 && toGo === 1) {
      // The persistent MATCH POINT / BEHIND pill already owns this teaching moment. A second floating
      // sentence can ghost behind board tiles or the rule banner in sampled frames, so the transition is
      // now carried by the pill's pop only.
      if (this.winStatusText.active && motionAllowed()) {
        this.tweens.killTweensOf(this.winStatusText);
        this.winStatusText.setScale(1);
        this.tweens.add({
          targets: this.winStatusText,
          scale: { from: 1.5, to: 1 },
          duration: 260,
          ease: 'Back.easeOut',
          onComplete: () => this.winStatusText?.setScale(1),
        });
      }
    }
    this.prevToGo = toGo;
  }


  /**
   * Tactical-OLED command-dock button. CHOMP is a FILLED primary (correct fill, ink label, soft glow);
   * SCAN is an OUTLINE secondary (surface fill, scan border + label) carrying a small "xN" charge badge.
   * `subLabel` is the SCAN charge text (badge); the data keys + hit wiring + onPress are preserved so
   * pulseDockButton / glowPulse / dispatch all keep working.
   */
export function buildCommandButton(this: GameScene,
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    subLabel: string,
    fill: number,
    textColor: number,
    onPress: () => void,
  ): Phaser.GameObjects.Container {
    const c = this.add.container(x, y).setDepth(5);
    const rad = Math.min(h * 0.34, 16);
    const filled = label === 'CHOMP' || label === 'MUNCH';
    const iconName: IconName = filled ? 'chomp' : 'scan';
    // Depleted information state (mockup HUD-TENSION dock: SCAN at ×0 drops to muted label/icon on a
    // flat tile fill with a hairline border, glow gone). Read by every paint closure below so hover/
    // press repaints stay depleted-styled; toggled via the container's 'setDepleted' data hook.
    let depleted = false;
    let unavailable = false;

    // Outer accent glow (filled buttons get a stronger halo; outline buttons a faint lift).
    const glow = this.add.graphics();
    const paintGlow = (on: boolean): void => {
      glow.clear();
      if (depleted || unavailable) return;
      // M5: the filled CHOMP primary gets a stronger, wider halo (bloom) than the outline SCAN button.
      glow.fillStyle(fill, (filled ? 0.3 : 0.1) + (on ? 0.14 : 0));
      const gm = filled ? 7 : 4;
      glow.fillRoundedRect(-w / 2 - gm, -h / 2 - (filled ? 5 : 2), w + gm * 2, h + gm * 2 + 4, rad + gm - 1);
    };
    paintGlow(false);

    const g = this.add.graphics();
    const paint = (pressed: boolean): void => {
      g.clear();
      if (unavailable) {
        g.fillStyle(0x131b2e, 0.92);
        g.fillRoundedRect(-w / 2, -h / 2, w, h, rad);
        g.lineStyle(1.25, 0x3a4a66, 1);
        g.strokeRoundedRect(-w / 2, -h / 2, w, h, rad);
      } else if (filled) {
        // M5 CHOMP BLOOM: a vertical gradient body (lighter crown → base) + an inner top sheen so the
        // primary verb reads glossy + appetising instead of flat; pressed darkens the whole body and the
        // pointer handlers seat the label/icon down for press depth. Static (no motion) — reduceMotion-safe.
        const crown = pressed ? shade(fill, -0.04) : shade(fill, 0.16);
        const base = pressed ? shade(fill, -0.14) : fill;
        g.fillGradientStyle(crown, crown, base, base, 1, 1, 1, 1);
        g.fillRoundedRect(-w / 2, -h / 2, w, h, rad);
        if (!pressed) {
          g.fillStyle(PALETTE.white, 0.14); // soft bloom band hugging the top edge
          g.fillRoundedRect(-w / 2 + 3, -h / 2 + 3, w - 6, h * 0.42, { tl: rad - 2, tr: rad - 2, bl: 0, br: 0 });
        }
      } else if (depleted) {
        g.fillStyle(PALETTE.tile, 1);
        g.fillRoundedRect(-w / 2, -h / 2, w, h, rad);
        g.lineStyle(1, PALETTE.line, 1);
        g.strokeRoundedRect(-w / 2, -h / 2, w, h, rad);
      } else {
        g.fillStyle(pressed ? PALETTE.tileHot : PALETTE.surface, 1);
        g.fillRoundedRect(-w / 2, -h / 2, w, h, rad);
        g.lineStyle(1.5, fill, 1);
        g.strokeRoundedRect(-w / 2, -h / 2, w, h, rad);
      }
    };
    paint(false);

    const labelFs = Math.max(15, Math.min(21, w * 0.16));
    const iconSize = labelFs * 1.25;
    const labelTxt = this.add
      .text(0, 0, label, { fontFamily: FONT.display, fontSize: `${labelFs}px`, color: hex(textColor), fontStyle: 'bold' })
      .setOrigin(0.5);
    if (filled) labelTxt.setStroke('#06311f', 1.5);
    const gap = 8;
    const pairW = iconSize + gap + labelTxt.width;
    const glyphText = icon(this, iconName, -pairW / 2 + iconSize / 2, 0, iconSize, textColor);
    labelTxt.setX(pairW / 2 - labelTxt.width / 2);
    const lockedHint = filled
      ? this.add
          .text(0, Math.min(22, h * 0.31), 'Move to fresh tile to CHOMP', {
            fontFamily: FONT.family,
            fontSize: `${Math.max(15, Math.min(16, w * 0.09))}px`,
            color: '#c7d3e8',
            fontStyle: '700',
            stroke: '#06111f',
            strokeThickness: 2,
          })
          .setOrigin(0.5)
          .setVisible(false)
      : undefined;
    c.add(lockedHint ? [glow, g, glyphText, labelTxt, lockedHint] : [glow, g, glyphText, labelTxt]);
    const applyVerbLabelState = (): void => {
      const blocked = unavailable || depleted;
      const displayText = depleted && !filled ? 'NO SCANS' : label;
      const labelColor = unavailable ? 0xc7d3e8 : blocked ? PALETTE.text : textColor;
      const showIcon = !blocked || (unavailable && filled);
      const currentIconName: IconName = unavailable && filled ? 'lock' : iconName;
      const currentIconSize = unavailable && filled ? iconSize * 0.86 : iconSize;
      const showLockedHint = unavailable && filled;
      labelTxt
        .setText(displayText)
        .setY(showLockedHint ? -8 : 0)
        .setColor(hex(labelColor))
        .setStroke(filled && !blocked ? '#06311f' : '#06111f', blocked ? 2 : filled ? 1.5 : 0);
      glyphText.setVisible(showIcon);
      glyphText.clear();
      if (showIcon) drawIcon(glyphText, currentIconName, currentIconSize, labelColor);
      const currentPairW = showIcon ? currentIconSize + gap + labelTxt.width : labelTxt.width;
      glyphText.setPosition(-currentPairW / 2 + currentIconSize / 2, showLockedHint ? -7 : 0);
      labelTxt.setX(showIcon ? currentPairW / 2 - labelTxt.width / 2 : 0);
      lockedHint?.setVisible(showLockedHint);
    };
    applyVerbLabelState();

    // SCAN charge badge — a small scan-bordered pill at the top-right corner showing remaining scans.
    let subText: Phaser.GameObjects.Text;
    if (!filled) {
      const badgeFs = Math.max(12, Math.min(14, w * 0.095));
      const badgeW = 40;
      const badgeH = 22;
      const bx = w / 2 - 20;
      const by = -h / 2 + 13;
      const badge = this.add.graphics();
      const paintBadge = (): void => {
        badge.clear();
        badge.fillStyle(depleted ? PALETTE.tileHot : PALETTE.surface2, 1);
        badge.fillRoundedRect(bx - badgeW / 2, by - badgeH / 2, badgeW, badgeH, badgeH / 2);
        badge.lineStyle(1.5, depleted ? PALETTE.line : fill, depleted ? 1 : 0.95);
        badge.strokeRoundedRect(bx - badgeW / 2, by - badgeH / 2, badgeW, badgeH, badgeH / 2);
      };
      paintBadge();
      subText = this.add
        .text(bx, by, subLabel, { fontFamily: FONT.display, fontSize: `${badgeFs}px`, color: hex(fill), fontStyle: 'bold' })
        .setOrigin(0.5);
      c.add([badge, subText]);
      // updateHud drives this each frame; internally edge-triggered so repaints only fire on a flip.
      c.setData('setDepleted', (on: boolean): void => {
        if (depleted === on) return;
        depleted = on;
        c.setData('depleted', depleted);
        paintGlow(false);
        paint(false);
        paintBadge();
        subText.setColor(hex(on ? PALETTE.textDim : fill));
        applyVerbLabelState();
      });
    } else {
      subText = this.add.text(0, 0, '', { fontFamily: FONT.family, fontSize: '1px' }).setVisible(false);
      c.add(subText);
      c.setData('setUnavailable', (on: boolean): void => {
        if (unavailable === on) return;
        unavailable = on;
        c.setData('unavailable', unavailable);
        paintGlow(false);
        paint(false);
        applyVerbLabelState();
      });
    }

    const hit = this.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    c.add(hit);
    c.setData('verb', label);
    c.setData('w', w);
    c.setData('h', h);
    c.setData('depleted', false);
    c.setData('unavailable', false);
    c.setData('subText', subText);
    if (label === 'SCAN') this.scanText = subText;
    const verbLabelBaseY = (): number => (unavailable && filled ? -7 : 0);
    hit.on('pointerover', () => {
      c.setScale(1.03);
      paintGlow(true);
    });
    hit.on('pointerout', () => {
      c.setScale(1);
      paintGlow(false);
    });
    hit.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
      ev?.stopPropagation?.();
      c.setScale(0.96);
      paint(true);
      const baseY = verbLabelBaseY();
      glyphText.setY(baseY + 2); // M5: deeper press-seat so the CHOMP button reads like it physically depresses
      labelTxt.setY(baseY + 2);
    });
    hit.on('pointerup', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
      ev?.stopPropagation?.();
      c.setScale(1);
      paint(false);
      paintGlow(false);
      const baseY = verbLabelBaseY();
      glyphText.setY(baseY);
      labelTxt.setY(baseY);
      if (unavailable) {
        this.showCommandHint('MOVE TO A FRESH TILE TO CHOMP', PALETTE.scan, 1400);
        return;
      }
      if (depleted) {
        this.showCommandHint('NO SCANS LEFT', PALETTE.textDim, 1200);
        return;
      }
      onPress();
    });
    return c;
  }


export function pulseDockButton(this: GameScene, btn: Phaser.GameObjects.Container, color: number, duration = 440): void {
    if (!btn.active) return;
    if (btn.getData('depleted') === true || btn.getData('unavailable') === true) return;
    const bw = typeof btn.getData('w') === 'number' ? (btn.getData('w') as number) : Math.min(this.scale.width * 0.3, 130);
    const bh = typeof btn.getData('h') === 'number' ? (btn.getData('h') as number) : 46;
    // Command buttons already sit close to the mobile safe edge. Use an inset rounded pulse instead of
    // the shared cell pulse's square outward halo, which reads like a debug/hazard frame on the dock.
    const inset = 4;
    const rad = Math.min(bh * 0.34, 16);
    const pulse = this.add.graphics();
    pulse.fillStyle(color, 0.08);
    pulse.fillRoundedRect(-bw / 2 + inset, -bh / 2 + inset, bw - inset * 2, bh - inset * 2, Math.max(6, rad - inset));
    pulse.lineStyle(2.5, color, 0.92);
    pulse.strokeRoundedRect(-bw / 2 + inset, -bh / 2 + inset, bw - inset * 2, bh - inset * 2, Math.max(6, rad - inset));
    btn.add(pulse);
    this.tweens.add({
      targets: pulse,
      alpha: 0,
      duration: Math.min(duration, motionAllowed() ? duration : 320),
      ease: 'Quad.easeOut',
      onComplete: () => pulse.destroy(),
    });
    const verb = typeof btn.getData('verb') === 'string' ? (btn.getData('verb') as string) : '';
    // S3 item 2: the SCAN!/CHOMP! verb float routes through the SINGLE anchored toast slot (just above
    // the slot line so it rises clear of the command hint), and is SUPPRESSED while the SKIPPED pill
    // owns the slot — one thing visible there at a time (kills the audit's pill+float dock stack).
    if (verb && !this.safePassBanner?.active) {
      floatText(this, this.toastSlotX || this.scale.width / 2, this.toastSlotY - 24, `${verb}!`, hex(color), 15, {
        throttleKey: `dock-${verb}`,
        throttleMs: 180,
      });
    }
    if (!motionAllowed()) return;

    const base = btn.scaleX || 1;
    this.tweens.killTweensOf(btn);
    this.tweens.add({
      targets: btn,
      scale: base * 1.045,
      duration: 70,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => {
        if (btn.active) btn.setScale(base);
      },
    });
  }


  /** Pause iconButton docked at the right edge of the rule banner. Mirrors the ESC / P keys. */
export function buildPauseButton(this: GameScene, x: number, y: number, size: number): void {
    const btn = iconButton(this, x, y, 'pause', () => this.openPause(), { size, color: PALETTE.dim, sfx: 'uiTap' });
    btn.setDepth(12_000);
  }


export function updateLegalMoveHints(this: GameScene, snap: Snapshot, pos: number, show: boolean): void {
    const clearHints = (): void => {
      for (const h of this.legalMoveHints) {
        this.tweens.killTweensOf(h);
        h.destroy();
      }
      this.legalMoveHints = [];
      this.legalMoveHintKey = '';
    };
    if (!show) {
      if (this.legalMoveHints.length > 0) clearHints();
      return;
    }
    const row = Math.floor(pos / this.cols);
    const col = pos % this.cols;
    const ids = [
      col > 0 ? pos - 1 : null,
      col < this.cols - 1 ? pos + 1 : null,
      row > 0 ? pos - this.cols : null,
      row < this.rows - 1 ? pos + this.cols : null,
    ].filter((id): id is number => id !== null);
    const legal = ids
      .filter((id) => {
        const cell = snap.cells[id];
        return !!cell && cell.kind !== 'wall' && !cell.armed;
      })
      .slice(0, 3);
    const key = `${pos}:${legal.join(',')}`;
    if (key === this.legalMoveHintKey && this.legalMoveHints.length > 0) return;
    clearHints();
    this.legalMoveHintKey = key;
    for (const id of legal) {
      const v = this.cellViews[id];
      if (!v?.container.active) continue;
      const g = this.add.graphics();
      g.fillStyle(PALETTE.scan, 0.1);
      g.fillRoundedRect(-v.w / 2 + 5, -v.h / 2 + 5, v.w - 10, v.h - 10, 9);
      g.lineStyle(5, PALETTE.scan, 1);
      g.strokeRoundedRect(-v.w / 2 + 5, -v.h / 2 + 5, v.w - 10, v.h - 10, 9);
      g.lineStyle(2, PALETTE.white, 0.72);
      g.strokeRoundedRect(-v.w / 2 + 12, -v.h / 2 + 12, v.w - 24, v.h - 24, 7);
      // Shape cue in addition to cyan: the legal step target carries a small white "move pin", so the
      // route affordance survives color-blind and low-saturation captures.
      const pinR = Math.max(4, Math.min(7, v.w * 0.08));
      const pinY = -v.h / 2 + Math.max(15, v.h * 0.24);
      g.fillStyle(PALETTE.white, 0.94);
      g.fillCircle(0, pinY, pinR);
      g.fillStyle(PALETTE.scan, 0.96);
      g.fillCircle(0, pinY, pinR * 0.48);
      g.lineStyle(Math.max(2, pinR * 0.34), PALETTE.white, 0.92);
      g.lineBetween(-pinR * 1.7, pinY + pinR * 1.85, 0, pinY + pinR * 3.05);
      g.lineBetween(pinR * 1.7, pinY + pinR * 1.85, 0, pinY + pinR * 3.05);
      v.container.add(g);
      this.legalMoveHints.push(g);
      if (motionAllowed()) this.tweens.add({ targets: g, scale: 1.025, duration: 560, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
  }


export function updateHud(this: GameScene, snap: Snapshot): void {
    const human = snap.players.find((p) => p.id === PLAYER);
    if (!human) return;
    if (human.combo > this.peakCombo) this.peakCombo = human.combo; // M3: track for stats/summary
    this.scoreText.setText(human.score.toLocaleString('en-US'));
    // VG-3b: draw the eye to the climbing number — scale-pop the SCORE readout whenever it rises so
    // the score increase reads in motion. Motion-gated; -1 = first draw.
    if (this.prevScore >= 0 && human.score > this.prevScore) this.pulseScoreReadout();
    // S3 item 6: a DROP (decoy CHOMP −60) flashes the readout hazard-red then eases back to player-cyan
    // so the loss reads as a loss, not a cyan "gain". Held even under reduced-motion (no pulse).
    else if (this.prevScore >= 0 && human.score < this.prevScore) this.flashScoreNegative();
    this.prevScore = human.score;
    const tier = comboTier(human.combo);
    const mult = comboMultiplier(human.combo);
    const heroStampActive = this.chainSpectacleObjs.size > 0;
    const comboLive = !heroStampActive && human.combo >= 1;
    // Center zone reads "⚡ ×N" over a "COMBO" eyebrow; the tier name fires as a milestone float, not here.
    this.comboText.setText(comboLive ? multGlyph(mult) : ''); // #46 standardized × glyph
    this.comboSubLabel?.setVisible(comboLive);
    if (comboLive) this.comboSubLabel?.setColor(hex(tier?.color ?? PALETTE.correct));
    // Lightning glyph rides just left of the combo readout while a combo is live, tinted to the tier.
    if (this.comboIcon) {
      const showBolt = comboLive && this.comboText.text.length > 0;
      this.comboIcon.setVisible(showBolt);
      if (showBolt) {
        this.comboIcon.clear();
        drawIcon(this.comboIcon, 'bolt', 16, tier?.color ?? PALETTE.correct);
        // Landscape: the ×N is left-origin (fixed left edge), so the bolt sits at a fixed rail-flush x;
        // portrait keeps the bolt glued to the left of the centred readout (grows with the text width).
        this.comboIcon.setPosition(
          isLandscape() ? RAIL_PAD + 8 : this.comboText.x - this.comboText.displayWidth / 2 - 11,
          this.comboText.y,
        );
      }
    }
    this.heartRowSet?.(human.lives);
    this.updateQuotaChip(human.eatenCorrect, snap.quota);
    // WARN-1: refresh the live rival readout every frame so the YOU/RIVAL gap is felt during play.
    this.updateRivalReadout(snap);
    // Synthesize the "how close am I to winning" verdict from quota-to-go + the AI margin.
    this.updateWinStatus(snap);

    // SCAN charge badge + button affordance — scansLeft===0 drops the dock button to the mockup's
    // depleted state (muted label/icon, flat tile fill, hairline border, ×0 badge). An information
    // state, not a tension state: it applies whenever scans are spent. Colors are owned by the
    // button's own setDepleted hook (edge-triggered inside) so hover/press repaints stay consistent.
    if (this.scanUnlocked) {
      this.scanText.setText(multGlyph(human.scansLeft)); // #46 standardized × glyph
      const setDepleted = this.scanButton.getData('setDepleted') as ((on: boolean) => void) | undefined;
      setDepleted?.(human.scansLeft <= 0);
    }
    const here = snap.cells[human.pos];
    const canChomp = Boolean(here && !here.eaten && here.kind !== 'wall');
    const setUnavailable = this.munchButton.getData('setUnavailable') as ((on: boolean) => void) | undefined;
    setUnavailable?.(!canChomp);
    this.munchButton.setAlpha(canChomp ? 1 : 0.82);
    this.updateLegalMoveHints(snap, human.pos, !canChomp);

    this.updateUnderText(snap, human.pos);
    this.updateSelectionRing(human.pos);

    const frac = Phaser.Math.Clamp(snap.timeLeftMs / this.timerMaxMs, 0, 1);
    if (this.timerVisible) {
      this.timerPillSet?.(frac, snap.timeLeftMs / 1000);
      this.updateTimerTension(snap.timeLeftMs);
    }
    // Timer LOW state tracks the kit's own solid-hazard threshold (<15%), independent of tension.
    this.updateTimerLowState(this.timerVisible && frac < 0.15 && snap.timeLeftMs > 0);

    // HUD-TENSION held state: human down to last life OR the timer in its final quarter.
    this.setTensionState(human.lives <= 1 || frac < 0.25);
    // Last-life heart pulse — evaluated AFTER setTensionState so the treatment lands the same frame.
    this.updateHeartTension(human.lives);
  }


  /**
   * HUD-TENSION held state. When the human is on their last life or the timer is low, hold a full-screen
   * red near-miss vignette (motion-gated pulse) and let the rival pill border go solid. Idempotent: only
   * (re)starts the veil tween on a real on→off edge so it never re-triggers every frame.
   */
export function setTensionState(this: GameScene, on: boolean): void {
    if (on === this.tensionOn) return;
    this.tensionOn = on;
    // #33: signal the panic on/off edge so Lane AUDIO can start/stop a heartbeat bed (it owns the sound).
    this.game.events.emit(PANIC_EVENT, on);
    // Warm backdrop shift (mockup parity): fade the plum wash in/out slowly — reduced-motion snaps
    // to the same calm static shift (a held state cue, never a flash either way).
    const warm = this.tensionWarmth;
    if (warm) {
      this.tweens.killTweensOf(warm);
      if (on) {
        warm.setVisible(true);
        if (motionAllowed()) {
          this.tweens.add({ targets: warm, alpha: 0.35, duration: 480, ease: 'Sine.easeOut' });
        } else {
          warm.setAlpha(0.35);
        }
      } else if (motionAllowed()) {
        this.tweens.add({
          targets: warm,
          alpha: 0,
          duration: 320,
          ease: 'Sine.easeIn',
          onComplete: () => warm.setVisible(false),
        });
      } else {
        warm.setAlpha(0).setVisible(false);
      }
    }
    const veil = this.tensionVeil;
    if (!veil) return;
    this.tweens.killTweensOf(veil);
    if (on) {
      veil.setVisible(true);
      if (motionAllowed()) {
        veil.setAlpha(0.7);
        this.tweens.add({
          targets: veil,
          alpha: { from: 0.55, to: 1 },
          duration: 620,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      } else {
        // State cue must be visible even under reduced motion (no pulse, held strong).
        veil.setAlpha(0.9);
      }
    } else {
      veil.setVisible(false).setAlpha(0);
    }
  }


  /**
   * HUD-TENSION heart treatment (mockup parity): while tension is held on the player's LAST life,
   * the single remaining heart burns brighter and gently pulses; spent hearts stay muted (heartRowSet
   * just repainted them). Reduced-motion: a static bright red heart, no pulse. Called every HUD frame
   * right AFTER heartRowSet's repaint so the brighter tint wins while the state is active; the pulse
   * tween itself is edge-triggered so it starts/stops exactly once per transition.
   */
export function updateHeartTension(this: GameScene, lives: number): void {
    const heart = this.heartGs[0];
    const active = this.tensionOn && lives === 1 && !!heart?.active;
    if (active && heart) {
      heart.clear();
      drawIcon(heart, 'heart', this.heartSize, shade(PALETTE.hazard, 0.22));
    }
    if (active === this.heartTensionOn) return;
    this.heartTensionOn = active;
    this.tensionHeartTween?.remove();
    this.tensionHeartTween = undefined;
    if (!heart) return;
    if (active && motionAllowed()) {
      this.tensionHeartTween = this.tweens.add({
        targets: heart,
        alpha: { from: 1, to: 0.45 },
        scale: { from: 1, to: 1.12 },
        duration: 420,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    } else if (!active && heart.active) {
      heart.setAlpha(1).setScale(1);
    }
  }


  /**
   * Timer LOW-state treatment (mockup parity): once the pill's fill has gone solid hazard (<15% —
   * the kit's own low threshold), back it with a soft red glow and let the whole pill (clock +
   * digits + bar) breathe. Edge-triggered; reduced-motion holds a static glow with no pulse; both
   * revert cleanly when the state ends (board end / time regained / new board).
   */
export function updateTimerLowState(this: GameScene, low: boolean): void {
    if (low === this.timerLowOn) return;
    this.timerLowOn = low;
    const glow = this.timerGlow;
    const pill = this.timerPillC;
    this.timerGlowTween?.remove();
    this.timerGlowTween = undefined;
    this.timerPillPulse?.remove();
    this.timerPillPulse = undefined;
    if (low) {
      glow?.setVisible(true).setAlpha(1);
      if (motionAllowed()) {
        if (glow?.active) {
          this.timerGlowTween = this.tweens.add({
            targets: glow,
            alpha: { from: 0.55, to: 1 },
            duration: 520,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1,
          });
        }
        if (pill?.active) {
          this.timerPillPulse = this.tweens.add({
            targets: pill,
            alpha: { from: 1, to: 0.7 },
            duration: 520,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1,
          });
        }
      }
    } else {
      glow?.setVisible(false);
      pill?.setAlpha(1);
    }
  }


  /**
   * J5 — combo readout pop + tier recolor. The text content is owned by updateHud(); this only
   * animates it on a real combo tick (the `combo` event), so we never tween every frame. Tier ramp:
   * white (just starting) -> green (x1.5) -> amber (x2) -> hot/red (x3), matching the multiplier
   * breakpoints used in updateHud. The scale pop is motion-gated (it returns to a 1.0 baseline either
   * way so the readout never sticks oversized when reduced-motion is on).
   */
export function pulseComboReadout(this: GameScene, combo: number): void {
    if (!this.comboText || !this.comboText.active) return;
    const color = comboTier(combo)?.color ?? PALETTE.white;
    this.comboText.setColor(hex(color));
    if (!motionAllowed()) {
      this.comboText.setScale(1);
      return;
    }
    // bigger pop the higher the combo climbs, capped so it never blows out of the HUD.
    const peak = 1 + Phaser.Math.Clamp(0.12 + combo * 0.03, 0.12, 0.4);
    this.tweens.killTweensOf(this.comboText);
    this.comboText.setScale(1);
    this.tweens.add({
      targets: this.comboText,
      scale: peak,
      duration: 90,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => this.comboText?.setScale(1),
    });
  }


  /**
   * VG-3b — SCORE-HUD tick. A quick scale-pop on the SCORE readout when the human's score climbs, so
   * the eye is drawn to the number rising (called from updateHud only on a real increase). The text is
   * left-anchored (origin 0,0.5) so the pop grows rightward from its edge and never crowds the centered
   * COMBO readout. Motion-gated — under reduced-motion we snap back to 1.0 and skip the tween.
   */
export function pulseScoreReadout(this: GameScene): void {
    if (!this.scoreText || !this.scoreText.active) return;
    if (!motionAllowed()) {
      this.scoreText.setScale(1);
      return;
    }
    this.tweens.killTweensOf(this.scoreText);
    this.scoreText.setScale(1);
    this.tweens.add({
      targets: this.scoreText,
      scale: 1.18,
      duration: 90,
      ease: 'Quad.easeOut',
      yoyo: true,
      onComplete: () => this.scoreText?.setScale(1),
    });
  }


  /**
   * S3 item 6 — negative SCORE tick. A score DROP (eating a decoy: −60) recolors the SCORE readout to
   * hazard-red and its glow to match, then eases the tint back to player-cyan over ~600ms so the loss
   * is felt as a loss, not a cyan gain. The color change itself is not motion (WCAG-safe) — it holds
   * red briefly and reverts even under reduced-motion (where the tween is skipped for an instant snap).
   */
export function flashScoreNegative(this: GameScene): void {
    const t = this.scoreText;
    if (!t?.active) return;
    this.tweens.killTweensOf(t);
    t.setScale(1);
    t.setColor(hex(PALETTE.hazard)).setShadow(0, 0, hex(PALETTE.hazard), 8, false, true);
    if (!motionAllowed()) {
      this.time.delayedCall(500, () => {
        if (t.active) t.setColor(hex(PALETTE.player)).setShadow(0, 0, hex(PALETTE.player), 8, false, true);
      });
      return;
    }
    // A quick recoil dip + a color ease back. Interpolate the tint each frame from hazard → player.
    const from = Phaser.Display.Color.IntegerToColor(PALETTE.hazard);
    const to = Phaser.Display.Color.IntegerToColor(PALETTE.player);
    const o = { v: 0 };
    this.tweens.add({
      targets: t,
      scale: { from: 1.14, to: 1 },
      duration: 140,
      ease: 'Quad.easeOut',
      onComplete: () => t.active && t.setScale(1),
    });
    this.tweens.add({
      targets: o,
      v: 1,
      duration: 600,
      delay: 120,
      ease: 'Sine.easeIn',
      onUpdate: () => {
        if (!t.active) return;
        const c = Phaser.Display.Color.Interpolate.ColorWithColor(from, to, 100, Math.round(o.v * 100));
        const col = Phaser.Display.Color.GetColor(c.r, c.g, c.b);
        t.setColor(hex(col)).setShadow(0, 0, hex(col), 8, false, true);
      },
      onComplete: () => t.active && t.setColor(hex(PALETTE.player)).setShadow(0, 0, hex(PALETTE.player), 8, false, true),
    });
  }


  /**
   * J3 — named combo milestone tiers. The full tier name is the persistent combo readout once the
   * player crosses a breakpoint; the event payoff stays non-textual so it cannot overlap the HUD,
   * SAFE-PASS coach line, or answer cells in the tight 360px portrait layout.
   */
export function showComboTierMilestone(this: GameScene, combo: number): void {
    const tier = comboTier(combo);
    if (!tier || combo !== tier.milestone) return;
    vignettePulse(this, tier.color, combo >= 7 ? 420 : 320, combo >= 7 ? 0.2 : 0.14);
    particleBurst(this, this.comboText.x, this.comboText.y + 10, tier.color, combo >= 7 ? 10 : 6);
    // VG-3c: a CROSSING a tier (HOT STREAK / BRAIN RUSH / MUNCHSTORM) should land harder than a
    // routine combo tick. Supersede pulseComboReadout's small pop with a bigger emphatic punch on the
    // tier name so the escalation reads as a non-text beat. Motion-gated; snaps back to 1.0 baseline.
    if (this.comboText?.active && motionAllowed()) {
      this.tweens.killTweensOf(this.comboText);
      this.comboText.setScale(1);
      this.tweens.add({
        targets: this.comboText,
        scale: combo >= 7 ? 1.5 : 1.34,
        duration: 120,
        ease: 'Back.easeOut',
        yoyo: true,
        onComplete: () => this.comboText?.setScale(1),
      });
    }
  }


  /**
   * Final-5s tension cue. Presentation-only (driven off the snapshot's read-only timeLeftMs — it
   * never advances the deterministic sim): pulse the timer bar (motion-gated) and tick a `countdown`
   * SFX at most ONCE per whole second of the last 5. We key the SFX on the integer second remaining
   * (tracked in lastCountdownSec) so a burst of frames in the same second can't re-trigger it.
   */
export function updateTimerTension(this: GameScene, timeLeftMs: number): void {
    const pill = this.timerPillC;
    // Outside the danger window: reset the per-second latch and clear any lingering pulse transform.
    if (timeLeftMs > 5000 || timeLeftMs <= 0) {
      if (this.lastCountdownSec !== -1) {
        this.lastCountdownSec = -1;
        this.timerThrobTween?.remove();
        this.timerThrobTween = undefined;
        pill?.setScale(1);
      }
      return;
    }
    // ceil so 4001..5000ms -> "5", 1..1000ms -> "1": one tick per whole second of the final five.
    const sec = Math.ceil(timeLeftMs / 1000);
    if (sec !== this.lastCountdownSec) {
      this.lastCountdownSec = sec;
      audio().playSfx('countdown', { volume: 0.85 });
      // #33 PANIC: signal Lane AUDIO's heartbeat (game-level event so it survives the scene). Lane AUDIO
      // OWNS the sound (pitch-rise + boom at 0); G1 only fires the whole-seconds-remaining (5..1). No-op
      // until AUDIO subscribes — never edits audio.ts.
      this.game.events.emit(HEARTBEAT_EVENT, sec);
      // #33 ESCALATION factor: 0 at "5", 1 at "1". Drives a punchier per-second timer throb AND a
      // faster/stronger breathing vignette so the final five read as PANIC, not steady caution.
      const k = Phaser.Math.Clamp((5 - sec) / 4, 0, 1);
      if (motionAllowed() && pill?.active) {
        // a single yoyo throb per second on the whole timer pill (low time → the pill is solid-hazard),
        // its peak escalating as the seconds run out. Tracked in its own field (NOT killTweensOf) so it
        // never kills the low-state alpha pulse.
        const peak = Phaser.Math.Linear(PANIC_PILL_PEAK.lo, PANIC_PILL_PEAK.hi, k);
        this.timerThrobTween?.remove();
        pill.setScale(1);
        this.timerThrobTween = this.tweens.add({
          targets: pill,
          scale: { from: 1, to: peak },
          duration: 130,
          ease: 'Quad.easeOut',
          yoyo: true,
          onComplete: () => pill.active && pill.setScale(1),
        });
        // Escalating breathing vignette: re-drive the ALREADY-HELD tension veil (setTensionState turned
        // it on well before the final 5s) to breathe faster + stronger each second. Safe from a tween
        // fight: setTensionState only (re)starts the veil pulse on an on/off EDGE, and tension stays ON
        // through the whole final-5s window — so this owns the veil pulse uncontested here.
        const veil = this.tensionVeil;
        if (veil?.active && veil.visible) {
          this.tweens.killTweensOf(veil);
          const period = Phaser.Math.Linear(PANIC_VEIL_MS.slow, PANIC_VEIL_MS.fast, k);
          const hi = Phaser.Math.Linear(PANIC_VEIL_ALPHA.lo, PANIC_VEIL_ALPHA.hi, k);
          veil.setAlpha(hi);
          this.tweens.add({
            targets: veil,
            alpha: { from: hi * 0.72, to: hi },
            duration: period,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1,
          });
        }
      }
    }
  }


  /** Mirror the cell under the human (label + scanned/eaten status) so the sprite never hides it. */
export function updateUnderText(this: GameScene, snap: Snapshot, pos: number): void {
    // Fix 3 (zero-overlap gate): the centered 'YOU ARE ON: …' status line and the 'GAUNTLET CHAIN ×N'
    // crescendo banner share the above-grid band and INTERSECT at 150% text scale. While any hero-stamp
    // (the crescendo/FLAWLESS banner) is showing, fade this status line out so the two never co-occupy
    // the band; fade it back the moment the stamp clears. Edge-triggered so it never re-tweens per frame.
    // S9 FIX-C (G0 AMATEUR 4 — teach card clipped 'YOU ARE ON' on mobile): the portrait teach-beat card
    // sits in the same above-grid band; while one is live (underTextYieldUntilMs window, armed by the
    // GameScene showTeachBeat wrapper) the status line yields exactly like it does for a hero stamp.
    const target = this.cellCenter(pos);
    const tokenInTransit =
      !!this.humanSprite?.active &&
      Phaser.Math.Distance.Between(this.humanSprite.x, this.humanSprite.y, target.x, target.y) > Math.max(3, Math.min(this.cellW, this.cellH) * 0.05);
    const activeHint = this.commandHintText?.text ?? '';
    const warningHintActive = !isLandscape() && this.time.now < this.hintHoldUntilMs && isWarningHintText(activeHint);
    const stampActive =
      this.chainSpectacleObjs.size > 0 || this.time.now < this.underTextYieldUntilMs || tokenInTransit || warningHintActive;
    if (stampActive !== this.underTextDimmed) {
      this.underTextDimmed = stampActive;
      if (this.underText?.active) {
        this.tweens.killTweensOf(this.underText);
        if (stampActive) {
          // Snap OUT instantly (not a tween): the crescendo fires a hitstop that sets tweens.timeScale
          // ~0 for its slow-mo, so a fade here would stall at full alpha during the exact peak frame the
          // collision must be gone. An immediate yield is also the right feel — the status line steps
          // aside the instant the hero banner lands. Restored with a gentle fade once the stamp clears
          // (no hitstop is active then, so the tween runs normally).
          this.underText.setAlpha(0);
          this.underTextBg?.clear();
        } else if (motionAllowed()) {
          this.tweens.add({ targets: this.underText, alpha: 1, duration: 220, ease: 'Sine.easeOut' });
        } else {
          this.underText.setAlpha(1);
        }
      }
    }
    // A transient showCommandHint (teach beat / idle nudge / doomed notice) owns the hint line while
    // held — without this, the per-frame standing-hint rewrite clobbers every transient hint on the
    // very next frame (verified live 2026-07-01: hints survived only ~16-18ms).
    const hintHeld = this.time.now < this.hintHoldUntilMs;
    const cell = snap.cells[pos];
    if (!cell || cell.kind === 'wall') {
      this.underText.setText('').setColor(hex(PALETTE.textDim));
      repaintUnderTextPlate(this);
      if (!hintHeld) this.commandHintText.setText('MOVE').setColor(hex(PALETTE.text));
      return;
    }
    const revealed = cell.revealedUntilMs > snap.timeMs && !cell.eaten;
    let underStatus = '';
    let hint = 'MOVE';
    let color: number = PALETTE.text;
    let standingDefault = false; // true only for the un-eaten/un-scanned how-to (the banner-hosted line)
    if (cell.eaten) {
      underStatus = cell.kind === 'correct' ? '  ✓ eaten' : '  ✗ eaten';
      hint = 'MOVE TO NEXT TILE';
      color = cell.kind === 'correct' ? PALETTE.correct : PALETTE.hazard;
    } else if (revealed) {
      underStatus = cell.kind === 'correct' ? '  SCAN: correct' : '  SCAN: decoy';
      hint =
        cell.kind === 'correct'
          ? 'SCAN: CORRECT - CHOMP'
          : 'SCAN: DECOY - MOVE AWAY';
      color = cell.kind === 'correct' ? PALETTE.correct : PALETTE.hazard;
    } else {
      // Un-eaten AND un-scanned: NEVER branch the hint on cell.kind here. Movement is free now
      // (P0.1 CHOMP-only), so a kind-keyed prompt ('CHOMP TO COMMIT' vs 'DECOY? MOVE AWAY') would
      // let the player read correct-vs-decoy for nothing — defeating SCAN and the no-ground-truth
      // pillar. Correct and decoy must show the SAME prompt; only SCAN or a CHOMP reveals the truth.
      hint = this.scanUnlocked ? 'CHOMP IF SURE · SCAN IF UNSURE' : 'CHOMP IF SURE · OR MOVE ON';
      standingDefault = true;
    }
    this.underText.setText(`YOU ARE ON: ${cell.displayText}${underStatus}`).setColor(hex(color));
    repaintUnderTextPlate(this);
    if (!hintHeld) {
      this.commandHintText.setPosition(this.toastSlotX || this.scale.width / 2, this.toastSlotY);
      this.commandHintBg?.clear();
      // M1 (mock-2): the standing how-to now lives in the RULE-banner coach subline (buildBanner sets
      // `hud.bannerCoachShown` when it rendered it). Drop the DUPLICATE floating copy while the banner
      // carries it — the floating slot stays clear for the CONTEXTUAL prompts (MOVE / NEXT TILE / SCAN
      // verdicts) and transient teach beats. If the banner couldn't host it (long rule / 150% scale),
      // the flag is false and the floating standing line is retained, so guidance is never lost.
      if (standingDefault && this.registry.get('hud.bannerCoachShown') === true) {
        this.commandHintText.setText('');
      } else {
        const railHint =
          standingDefault && isLandscape()
            ? this.scanUnlocked
              ? 'SCAN OR CHOMP'
              : 'CHOMP OR MOVE'
            : hint;
        this.commandHintText.setText(railHint).setColor(hex(PALETTE.white));
      }
    }
  }


export function updateSelectionRing(this: GameScene, pos: number): void {
    if (!this.selectionRing) return;
    const c = this.cellCenter(pos);
    const pad = 4;
    this.selectionRing.clear();
    this.selectionRing.lineStyle(4, PALETTE.player, 0.95);
    this.selectionRing.strokeRoundedRect(
      c.x - this.cellW / 2 + pad,
      c.y - this.cellH / 2 + pad,
      this.cellW - pad * 2,
      this.cellH - pad * 2,
      8,
    );
  }


export function showCommandHint(this: GameScene, message: string, color: number = PALETTE.scan, holdMs = 3500): void {
    this.hintHoldUntilMs = this.time.now + holdMs; // keep the per-frame standing rewrite off the line
    // S9 FIX-C caption-band arbiter: a coach caption OUTRANKS the cosmetic SCAN tooltip — retire a live
    // bubble immediately so the two can never stack in the same band (G0 AMATEURs 4-5).
    this.dismissScanTeachBubble();
    const t = this.commandHintText;
    if (!t) return;
    const warningLane = !isLandscape() && isWarningHintText(message);
    t.setText(message).setColor(hex(color));
    if (warningLane) {
      const boardBottom = this.gridY + this.cellH * this.rows;
      const timerY = this.timerPillC?.y ?? this.toastSlotY + 48;
      const y = Math.min(timerY - 36, boardBottom + 64);
      t.setPosition(this.toastSlotX || this.scale.width / 2, y);
      const padX = 14;
      const padY = 7;
      const bgW = Math.min(this.boardW * 0.88, t.width + padX * 2);
      const bgH = t.height + padY * 2;
      const bg = this.commandHintBg;
      bg?.clear();
      bg?.fillStyle(PALETTE.void, 0.86);
      bg?.fillRoundedRect(t.x - bgW / 2, t.y - bgH / 2, bgW, bgH, 12);
      bg?.lineStyle(1.5, color, 0.75);
      bg?.strokeRoundedRect(t.x - bgW / 2, t.y - bgH / 2, bgW, bgH, 12);
    } else {
      t.setPosition(this.toastSlotX || this.scale.width / 2, this.toastSlotY);
      this.commandHintBg?.clear();
    }
    // BUG FIX (found via frozen-frame capture, 2026-07-01): this used popTween, whose second phase
    // COLLAPSES its target to scale 0 / alpha 0 — so every transient hint (teach beats, DANGER
    // warnings, idle nudges) blipped for ~150ms and then the hint line was INVISIBLE for the rest
    // of the board (verified live: alpha 0 / scale 0 while the "held" text sat unreadable).
    // Replace with a non-collapsing emphasis pop that always restores the line to full presence.
    this.tweens.killTweensOf(t);
    t.setScale(1).setAlpha(1);
    if (motionAllowed()) {
      this.tweens.add({
        targets: t,
        scale: { from: 1, to: 1.06 },
        duration: 90,
        ease: 'Quad.easeOut',
        yoyo: true,
        onComplete: () => {
          if (t.active) t.setScale(1);
        },
      });
    }
  }


export function showWarningHint(this: GameScene, key: string, message: string, color: number, cooldownMs = 1400): void {
    const now = this.time.now;
    if (now < (this.warningHintAt.get(key) ?? -Infinity)) return;
    // SAFETY OUTRANKS COSMETIC (gap fix): a 'DANGER: HAZARD INCOMING' / LIAR / SCRAMBLER warning must
    // never be written to a commandHintText that the SKIPPED pill has hidden. During a sustained
    // Gauntlet route the pill re-arms every skip, so without this a warning (and its cooldown) would
    // burn silently under the pill. Retire the pill NOW and hand the slot back to the hint line BEFORE
    // we write — and only stamp the cooldown once the line is guaranteed visible (the text will show).
    this.dismissSafePassPill();
    this.warningHintAt.set(key, now + cooldownMs);
    this.showCommandHint(message, color);
  }


  /**
   * Retire the SKIPPED pill immediately and hand the ONE toast slot back to the persistent command-hint
   * line. Used when a safety warning must show (a warning outranks the cosmetic skip celebration) so the
   * hint line is never left hidden under the pill. No-op when the pill isn't up (but still idempotently
   * ensures the hint line is visible). Mirrors the pill's own 720ms cleanup, minus the fade — a safety
   * cue should not wait on a cosmetic dismiss.
   */
export function dismissSafePassPill(this: GameScene): void {
    const banner = this.safePassBanner;
    this.safePassResetTimer?.remove(false);
    this.safePassResetTimer = undefined;
    this.safePassBanner = undefined;
    this.safePassBannerText = undefined;
    this.safePassBannerBg = undefined;
    this.safePassCount = 0;
    if (banner) {
      this.tweens.killTweensOf(banner);
      banner.destroy();
    }
    // Slot is free again — restore the persistent command-hint line the pill hid (item 2).
    this.commandHintText?.setVisible(true);
  }


export function showSafePassBanner(this: GameScene): void {
    this.safePassCount += 1;
    this.safePassResetTimer?.remove(false);
    // PRIMED cue: once the human has traversed enough armed decoys (Core's gauntletArmThreshold), the
    // NEXT correct CHOMP scores the Gauntlet ×mult. Make that pending reward explicit BEFORE the
    // THREADED IT payoff so a first-time viewer can see risky routing has armed a bonus. armedTraversals
    // is the authoritative Core gate (resets on any munch), not the renderer's 720ms streak counter.
    const armed = this.sim.players[PLAYER]?.armedTraversals ?? 0;
    const primed = armed >= SCORING.gauntletArmThreshold;
    // One primed cue only: the dock-slot pill is the persistent state indicator. The older in-field chip
    // duplicated NEXT CHOMP and clipped the board edge on phone stills, so the board itself stays clean.
    this.primedFieldCueShown = primed;
    const base = this.safePassCount > 1 ? `SKIPPED ${multGlyph(this.safePassCount)}` : 'SKIPPED IT'; // #46 standardized × glyph
    const text = primed ? `NEXT CHOMP ${multGlyph(SCORING.gauntletMult)}` : base;
    // Primed swaps the cue from scan-cyan (a neutral "free pass") to armed-amber — the same color the
    // THREADED IT payoff and the armed padlocks use, so the eye connects routing → the amber bonus.
    const accent = primed ? PALETTE.armed : PALETTE.scan;
    const textColor = primed ? hex(PALETTE.gold) : hex(PALETTE.white);
    // S9 FIX-C: the pill anchors on the SAME slot point as the hint line (rail-docked in landscape).
    const x = this.toastSlotX || this.scale.width / 2;
    // S3 item 2: occupy the ONE anchored toast slot (dock-top band). While the pill is up, HIDE the
    // persistent command-hint line so the redundant LOCKED text can never sit under the SKIPPED banner
    // — one thing visible in the slot at a time. Restored on the pill's cleanup below.
    const y = this.toastSlotY;
    this.commandHintText?.setVisible(false);
    // The hero status pill also outranks the SCAN tooltip (G0 AMATEUR 6: pill/tooltip/coach triple-stack).
    this.dismissScanTeachBubble();

    if (!this.safePassBanner?.active) {
      this.safePassBannerBg = this.add.graphics();
      this.safePassBannerText = this.add
        .text(0, 0, text, {
          fontFamily: FONT.display,
          fontSize: `${typePx(this, 'body', { vw: 0.042, min: 13 })}px`,
          color: textColor,
          fontStyle: 'bold',
          stroke: '#06111f',
          strokeThickness: 4,
        })
        .setOrigin(0.5);
      this.safePassBanner = this.add
        .container(x, y, [this.safePassBannerBg, this.safePassBannerText])
        .setDepth(9_600)
        .setAlpha(0);
      this.tweens.add({ targets: this.safePassBanner, alpha: 1, duration: 110, ease: 'Quad.easeOut' });
    } else {
      this.tweens.killTweensOf(this.safePassBanner);
      this.safePassBanner.setPosition(x, y).setAlpha(1).setScale(1);
      if (motionAllowed()) {
        this.tweens.add({
          targets: this.safePassBanner,
          // Reaching primed gets a punchier pop than a routine skip so the state change is felt.
          scale: { from: primed ? 1.12 : 1.04, to: 1 },
          duration: primed ? 150 : 120,
          ease: 'Back.easeOut',
        });
      }
    }

    this.safePassBannerText?.setText(text);
    this.safePassBannerText?.setColor(textColor); // primed state can flip between passes — recolor each time
    if (this.safePassBannerBg && this.safePassBannerText) {
      const w = this.safePassBannerText.width + 20;
      const h = this.safePassBannerText.height + 8;
      this.safePassBannerBg.clear();
      this.safePassBannerBg.fillStyle(0x0a0b12, primed ? 0.88 : 0.76);
      this.safePassBannerBg.fillRoundedRect(-w / 2, -h / 2, w, h, h / 2);
      this.safePassBannerBg.lineStyle(primed ? 3 : 2, primed ? PALETTE.gold : accent, primed ? 0.98 : 0.84);
      this.safePassBannerBg.strokeRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    }

    this.safePassResetTimer = this.time.delayedCall(720, () => {
      this.safePassCount = 0;
      const banner = this.safePassBanner;
      this.safePassBanner = undefined;
      this.safePassBannerText = undefined;
      this.safePassBannerBg = undefined;
      this.safePassResetTimer = undefined;
      const restoreHint = (): void => {
        this.commandHintText?.setVisible(true);
      };
      if (!banner?.active) {
        restoreHint();
        return;
      }
      this.tweens.add({
        targets: banner,
        alpha: 0,
        y: y - 12,
        duration: 180,
        ease: 'Quad.easeIn',
        onComplete: () => {
          banner.destroy();
          restoreHint();
        },
      });
    });
  }


  /**
   * Ephemeral in-field bonus cue: a small amber `NEXT CHOMP ×MULT` chip that pops just above the human
   * muncher (primary FOV, not the thumb-occluded bottom bar), floats up, and fades over ~1s. Never
   * persistent — it self-destroys so it can't cover answer cells (the bottom-bar banner remains the
   * standing state indicator). Multiplier is read from Core config. Motion-gated: reduced-motion gets
   * a static fade-in/hold/out with no pop or float. Depth sits above the board but under the HUD banners.
   */
export function showPrimedFieldCue(this: GameScene): void {
    const me = this.sim.players[PLAYER];
    if (!me) return;
    const c = this.cellCenter(me.pos);
    const boardBottom = this.gridY + this.cellH * this.rows;
    const startY = Phaser.Math.Clamp(
      c.y - this.cellH * 0.55,
      this.gridY + this.cellH * 0.18,
      boardBottom - this.cellH * 0.18,
    );
    const label = `NEXT CHOMP ${multGlyph(SCORING.gauntletMult)}`; // #46 standardized × glyph
    const txt = this.add
      .text(0, 0, label, {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'body', { vw: 0.036, min: 12, max: 16 })}px`,
        color: hex(PALETTE.gold),
        fontStyle: 'bold',
        stroke: '#06111f',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    const w = txt.width + 16;
    const h = txt.height + 8;
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0b12, 0.88);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    bg.lineStyle(2.5, PALETTE.gold, 0.98);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, h / 2);
    const cx = Phaser.Math.Clamp(c.x, w / 2 + 6, this.scale.width - w / 2 - 6);
    const chip = this.add.container(cx, startY, [bg, txt]).setDepth(9_200).setAlpha(0);
    if (motionAllowed()) {
      chip.setScale(0.6);
      this.tweens.add({
        targets: chip,
        scale: { from: 0.6, to: 1 },
        alpha: { from: 0, to: 1 },
        duration: 200,
        ease: 'Back.easeOut',
      });
      this.tweens.add({
        targets: chip,
        y: startY - Math.min(18, this.cellH * 0.16),
        alpha: 0,
        delay: 620,
        duration: 560,
        ease: 'Sine.easeIn',
        onComplete: () => chip.destroy(),
      });
    } else {
      this.tweens.add({
        targets: chip,
        alpha: { from: 0, to: 1 },
        duration: 140,
        yoyo: true,
        hold: 720,
        onComplete: () => chip.destroy(),
      });
    }
  }
